import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
} from 'react-native';
import SvgIcon from '../components/SvgIcon';
import { useNavigation, useRoute } from '@react-navigation/native';
import colors from '../theme/colors';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// Default icons for missing photos
const BRIDE_ICON = 'https://kalyanamitra.in/assets/ps-bride-UWGtbgl1.png';
const GROOM_ICON = 'https://kalyanamitra.in/assets/ps-groom-D-L2Y5H0.png';

const MemberProfileScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { user } = useAuth();
  
  // Use memberId from route params (for mediator viewing member) or from logged-in user (for member viewing own profile)
  const memberId = route.params?.memberId || user?.memberId;
  
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (memberId) {
      loadProfile();
    }
  }, [memberId]);

  // Listen for shouldRefresh parameter from ProfileDetailScreen
  useEffect(() => {
    if (route.params?.shouldRefresh) {
      setHasChanges(true); // Track that changes occurred
      loadProfile();
      // Clear the shouldRefresh param to avoid reloading on subsequent visits
      navigation.setParams({ shouldRefresh: false });
    }
  }, [route.params?.shouldRefresh]);

  // Add focus listener to reload profile when returning from other screens
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      // Only reload if we have a memberId to avoid errors during logout
      if (memberId) {
        loadProfile();
      }
    });
    return unsubscribe;
  }, [navigation, memberId]);

  const loadProfile = async () => {
    setIsLoading(true);
    try {
      if (!memberId) {
        Alert.alert('Error', 'Member ID not found');
        return;
      }

      const response = await api.get(`/api/EnhancedMember/shortProfile/${memberId}?includePhotos=true`);
      const data = response.data?.data || response.data;
      console.log('Member Profile Data:', data);
      setProfileData(data);
    } catch (error) {
      console.error('Error loading profile:', error);
      Alert.alert('Error', 'Failed to load profile details');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleViewFullProfile = () => {
    if (profileData?.memberId) {
      // Use current route name as returnScreen to preserve Member vs Mediator context
      navigation.push('ProfileDetail', { 
        memberId: profileData.memberId,
        returnScreen: route.name || 'MemberProfile'
      });
    }
  };

  const handleViewSharedProfiles = () => {
    if (profileData?.memberId) {
      navigation.push('SharedProfiles', { memberId: profileData.memberId });
    }
  };

  const handleGoBack = () => {
    if (hasChanges) {
      // Signal the previous screen to reload by setting a param
      navigation.navigate({
        name: route.params?.returnScreen || 'Profiles',
        params: { shouldRefresh: true },
        merge: true,
      });
    } else {
      navigation.goBack();
    }
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.accent} />
        <Text style={styles.loadingText}>Loading profile...</Text>
      </View>
    );
  }

  if (!profileData) {
    return (
      <View style={styles.emptyContainer}>
        <SvgIcon name="person-outline" size={80} color={colors.textSecondary} />
        <Text style={styles.emptyText}>Profile not found</Text>
      </View>
    );
  }

  const photos = profileData.photos || [];
  const currentPhoto = photos[activePhotoIndex];
  const defaultIcon = profileData.gender === 'Male' ? GROOM_ICON : BRIDE_ICON;

  // Check if this is member viewing their own profile (no back button needed)
  // vs mediator viewing a member profile (back button needed)
  const isOwnProfile = user?.userType === 'Member' && !route.params?.memberId;

  return (
    <ScrollView style={styles.container}>
      <View style={styles.content}>
        {/* Back Button - only show for mediators or when viewing shared profiles */}
        {!isOwnProfile && (
          <TouchableOpacity 
            style={styles.backButton}
            onPress={handleGoBack}
          >
            <SvgIcon name="arrow-back" size={24} color={colors.primary} />
          </TouchableOpacity>
        )}
        
        {/* Profile Header */}
        <View style={styles.header}>
          <View style={styles.photoContainer}>
            <Image
              source={{ uri: currentPhoto?.securedUrl || defaultIcon }}
              style={styles.profilePhoto}
            />
            
            {/* Photo Thumbnails Overlay */}
            {photos.length > 1 && (
              <View style={styles.thumbnailOverlay}>
                <ScrollView 
                  horizontal 
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.thumbnailContainer}
                >
                  {photos.map((photo, index) => (
                    <TouchableOpacity
                      key={photo.photoId}
                      onPress={() => setActivePhotoIndex(index)}
                      style={[
                        styles.thumbnail,
                        activePhotoIndex === index && styles.activeThumbnail
                      ]}
                    >
                      <Image
                        source={{ uri: photo.securedUrl }}
                        style={styles.thumbnailImage}
                      />
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            )}
          </View>

          {/* Profile Name and Basic Info */}
          <View style={styles.profileInfo}>
            <Text style={styles.profileName}>
              {profileData.name} {profileData.surName}
            </Text>
            
            <View style={styles.headerInfoRow}>
              <Text style={styles.headerInfoText}>
                {calculateAge(profileData.dateOfBirth)} years
              </Text>
              <Text style={styles.headerInfoText}> • </Text>
              <Text style={styles.headerInfoText}>
                {profileData.profession || 'Not Specified'}
              </Text>
              <Text style={styles.headerInfoText}> • </Text>
              <Text style={styles.headerInfoText}>
                {profileData.highestEducationQualification || 'N/A'}
              </Text>
            </View>

            <View style={styles.headerInfoRow}>
              <SvgIcon name="location-outline" size={14} color={colors.accent} />
              <Text style={styles.headerInfoText}>
                {profileData.city}{profileData.state ? `, ${profileData.state}` : ''}{profileData.country ? `, ${profileData.country}` : ''}
              </Text>
            </View>
            
            {/* Profile Completion */}
            {profileData.profileCompletionPercentage !== undefined && (
              <View style={styles.completionContainer}>
                <View style={styles.completionHeader}>
                  <Text style={styles.completionLabel}>Profile Completion</Text>
                  <Text style={styles.completionPercentText}>
                    {profileData.profileCompletionPercentage}%
                  </Text>
                </View>
                <View style={styles.progressBarBg}>
                  <View 
                    style={[
                      styles.progressBarFill, 
                      { width: `${profileData.profileCompletionPercentage}%` }
                    ]} 
                  />
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.buttonsContainer}>
          <TouchableOpacity 
            style={[styles.actionButton, styles.viewFullButton]}
            onPress={handleViewFullProfile}
          >
            <Text style={styles.actionButtonText}>View Full Profile</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={[styles.actionButton, styles.viewSharedButton]}
            onPress={handleViewSharedProfiles}
          >
            <Text style={styles.actionButtonText}>View Shared</Text>
          </TouchableOpacity>
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.primary,
  },
  backButton: {
    position: 'absolute',
    left: 10,
    top: 10,
    padding: 8,
    zIndex: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.primary,
  },
  loadingText: {
    color: colors.white,
    marginTop: 10,
    fontSize: 16,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.primary,
  },
  emptyText: {
    fontSize: 18,
    color: colors.white,
    marginTop: 20,
  },
  content: {
    paddingBottom: 10,
  },
  header: {
    backgroundColor: '#0F3D4A',
    marginBottom: 12,
  },
  photoContainer: {
    position: 'relative',
    width: '100%',
    aspectRatio: 1,
    overflow: 'hidden',
  },
  profilePhoto: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  verifiedBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 4,
  },
  thumbnailOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    paddingVertical: 8,
    paddingHorizontal: 5,
  },
  thumbnailContainer: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingHorizontal: 5,
  },
  thumbnail: {
    width: 50,
    height: 50,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: 'transparent',
    marginHorizontal: 4,
  },
  activeThumbnail: {
    borderColor: colors.accent,
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
    borderRadius: 6,
  },
  profileInfo: {
    marginTop: 10,
    paddingHorizontal: 20,
    paddingBottom: 8,
    alignItems: 'center',
  },
  profileName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.white,
    marginBottom: 6,
    textAlign: 'center',
  },
  headerInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
    justifyContent: 'center',
  },
  headerInfoText: {
    fontSize: 14,
    color: '#E0E0E0',
  },
  completionContainer: {
    marginBottom:5,
    marginTop: 5,
    width: '100%',
  },
  completionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  completionLabel: {
    fontSize: 13,
    color: colors.accent,
  },
  completionPercentText: {
    fontSize: 13,
    color: colors.accent,
    fontWeight: '600',
  },
  progressBarBg: {
    height: 8,
    backgroundColor: '#1A5A6B',
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: colors.accent,
    borderRadius: 4,
  },
  buttonsContainer: {
    flexDirection: 'row',
    gap: 12,
    marginHorizontal: 20,
    marginBottom: 12,
  },
  actionButton: {
    flex: 1,
    borderRadius: 12,
    paddingVertical: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  viewFullButton: {
    backgroundColor: colors.accent,
  },
  viewSharedButton: {
    backgroundColor: colors.secondary,
  },
  actionButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
});

export default MemberProfileScreen;
